import { artRng, Bitmap, OUTLINE, shade, type RGB } from "./bitmap";

/** A leafy bush (from the mocks), radius r. */
export function leafy(r: number, seed = 0, c: RGB = [62, 132, 80]): Bitmap {
  const rnd = artRng(seed * 7919 + r);
  const w = r * 2 + 3;
  const im = new Bitmap(w, w);
  for (let k = 0; k < 7; k++) {
    const a = rnd() * Math.PI * 2;
    const ox = Math.cos(a) * r * 0.45;
    const oy = Math.sin(a) * r * 0.35 - 1;
    const rad = r * (0.45 + rnd() * 0.25);
    im.ellipse(w / 2 + ox - rad, w / 2 + oy - rad * 0.85, w / 2 + ox + rad, w / 2 + oy + rad * 0.85, shade(c, 0.85 + rnd() * 0.3));
  }
  im.ellipse(w / 2 - r * 0.5, w / 2 - r * 0.7, w / 2, w / 2 - r * 0.2, shade(c, 1.35));
  return im.outlined(OUTLINE);
}

/** Garden plants for the rooftop garden, grown in stages 0..3. */
export const GARDEN_PLANTS: { id: string; name: string; leaf: RGB; flower?: RGB }[] = [
  { id: "sunflower", name: "Sunflowers", leaf: [70, 140, 70], flower: [250, 200, 60] },
  { id: "tomato", name: "Tomatoes", leaf: [60, 130, 70], flower: [220, 60, 50] },
  { id: "lavender", name: "Lavender", leaf: [100, 140, 110], flower: [150, 110, 200] },
  { id: "rose", name: "Roses", leaf: [50, 110, 60], flower: [220, 70, 100] },
  { id: "fern", name: "Ferns", leaf: [60, 150, 80] },
  { id: "bamboo", name: "Bamboo", leaf: [120, 170, 80] },
  { id: "tulip", name: "Tulips", leaf: [70, 150, 80], flower: [240, 90, 70] },
  { id: "herbs", name: "Herbs", leaf: [90, 160, 90], flower: [240, 240, 220] },
  { id: "cactus", name: "Cacti", leaf: [80, 140, 90], flower: [240, 120, 160] },
  { id: "lemon", name: "Lemon tree", leaf: [60, 130, 70], flower: [250, 230, 80] },
  { id: "pumpkin", name: "Pumpkins", leaf: [80, 140, 70], flower: [230, 130, 40] },
  { id: "bonsai", name: "Bonsai", leaf: [50, 110, 70] },
  { id: "hydrangea", name: "Hydrangeas", leaf: [60, 130, 80], flower: [110, 150, 230] },
  { id: "strawberry", name: "Strawberries", leaf: [60, 140, 70], flower: [230, 50, 60] },
  { id: "moss", name: "Moss garden", leaf: [110, 160, 80] },
  { id: "wildflowers", name: "Wildflowers", leaf: [90, 150, 80], flower: [250, 220, 120] },
];

export function gardenPlant(id: string, stage: number, seed = 0): Bitmap {
  const p = GARDEN_PLANTS.find((x) => x.id === id) ?? GARDEN_PLANTS[0];
  const r = 2 + Math.min(3, stage) * 1.5;
  const im = leafy(Math.round(r), seed, p.leaf);
  if (p.flower && stage >= 2) {
    const rnd = artRng(seed + 99);
    for (let k = 0; k < 2 + stage * 2; k++) {
      const x = 2 + Math.floor(rnd() * (im.w - 4));
      const y = 2 + Math.floor(rnd() * (im.h / 2));
      if (im.alpha(x, y)) im.box(x, y, x + 1, y + 1, p.flower);
    }
  }
  return im;
}
